import { ErrorHandler, Injectable, isDevMode } from '@angular/core';

/**
 * Recovers a tab whose lazy chunks no longer exist on the server.
 *
 * A deploy renames every chunk it changed, so a tab that was already open asks for a file
 * that has been deleted. Worse, nginx answers unknown paths with `index.html` for the sake
 * of client-side routing, so the browser is handed HTML with a 200 and tries to run it as
 * JavaScript — the page simply breaks, with no hint that a deploy is the cause.
 *
 * Reloading fetches the current `index.html` and, with it, the current chunk names.
 *
 * The reload happens **once**. If the fresh copy fails the same way the cause is not
 * staleness, and a handler that reloads on every error would put the app in a refresh loop
 * that is impossible to escape or to diagnose. The flag lives in `sessionStorage` so it is
 * scoped to the tab and is gone by the next visit.
 */
@Injectable()
export class ChunkErrorHandler implements ErrorHandler {
  private static readonly ATTEMPTED = 'chunkReloadAttempted';

  handleError(error: unknown): void {
    if (this.isChunkLoadError(error) && !isDevMode()) {
      let alreadyTried = true;
      try {
        alreadyTried = sessionStorage.getItem(ChunkErrorHandler.ATTEMPTED) === 'true';
        sessionStorage.setItem(ChunkErrorHandler.ATTEMPTED, 'true');
      } catch {
        // Private browsing, or storage the user has blocked. Reloading blind could loop,
        // so treat an unreadable flag as "already tried" and report the error instead.
      }

      if (!alreadyTried) {
        console.warn('A lazy chunk is missing, which a deploy causes. Reloading once.');
        window.location.reload();
        return;
      }
    }

    console.error(error);
  }

  /**
   * Webpack names this error, and the router wraps it before it reaches here — so the
   * message is matched as well as the name, and the cause chain is followed.
   */
  private isChunkLoadError(error: unknown): boolean {
    const seen = new Set<unknown>();
    let current: unknown = error;

    while (current && typeof current === 'object' && !seen.has(current)) {
      seen.add(current);
      const wrapper = current as {
        name?: unknown;
        message?: unknown;
        cause?: unknown;
        rejection?: unknown;
        error?: unknown;
      };
      const message = String(wrapper.message ?? '');
      if (wrapper.name === 'ChunkLoadError' || /Loading chunk .* failed|dynamically imported module/i.test(message)) {
        return true;
      }
      current = wrapper.cause ?? wrapper.rejection ?? wrapper.error;
    }
    return false;
  }
}
